import { useState, useMemo, useCallback } from 'react';
import { Phone, Check, Calculator } from 'lucide-react';
import { SEOHead, getBreadcrumbSchema } from './SEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICING = {
  hookRate: 150,
  enroutePerMile: 4,
  loadedPerMile: 5,
  roadsideFlat: 75,
};

const SERVICE_AREAS = ["Orangevale", "Citrus Heights", "Folsom", "Rancho Cordova", "Roseville", "Rocklin", "Granite Bay", "El Dorado Hills"];

const INPUT_CLASS = "w-full bg-zinc-800 border border-zinc-700 rounded-md px-4 py-3 text-white focus:border-red-600 focus:outline-none";

function calcEstimate(serviceType, enrouteMiles, loadedMiles) {
  if (serviceType === 'roadside') return PRICING.roadsideFlat;
  if (serviceType === 'towing' || serviceType === 'hauling' || serviceType === 'load-transfer') {
    const enroute = parseFloat(enrouteMiles) || 0;
    const loaded = parseFloat(loadedMiles) || 0;
    return PRICING.hookRate + (enroute * PRICING.enroutePerMile) + (loaded * PRICING.loadedPerMile);
  }
  return null;
}

function QuoteSuccess() {
  return (
    <div className="bg-zinc-900 border border-green-600/50 rounded-xl p-8 text-center" data-testid="quote-success">
      <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-white" /></div>
      <h2 className="text-2xl font-bold text-white mb-2">Quote Request Submitted!</h2>
      <p className="text-gray-400">We'll get back to you shortly. For immediate help, call <a href="tel:+19162343923" className="text-red-600 font-semibold">(916) 234-3923</a>.</p>
    </div>
  );
}

function MileageEstimate({ form, estimate }) {
  return (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-5 space-y-4" data-testid="mileage-section">
      <div className="flex items-center gap-2 mb-2">
        <Calculator className="w-5 h-5 text-red-600" />
        <h3 className="text-sm font-semibold text-white">Mileage Estimate</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-gray-400 text-sm mb-1 block">Enroute Miles <span className="text-gray-500">($4/mile)</span></label>
          <input name="enroute_miles" type="number" min="0" step="0.1" value={form.enroute_miles} onChange={form.onChange} placeholder="Miles to your location" className={INPUT_CLASS} data-testid="input-enroute-miles" />
        </div>
        <div>
          <label className="text-gray-400 text-sm mb-1 block">Loaded Miles <span className="text-gray-500">($5/mile)</span></label>
          <input name="loaded_miles" type="number" min="0" step="0.1" value={form.loaded_miles} onChange={form.onChange} placeholder="Miles to destination" className={INPUT_CLASS} data-testid="input-loaded-miles" />
        </div>
      </div>
      {estimate !== null && (
        <div className="bg-zinc-900 border border-red-600/30 rounded-md p-4 text-center" data-testid="price-estimate">
          <p className="text-gray-400 text-sm mb-1">Estimated Total</p>
          <p className="text-3xl font-bold text-red-600">${estimate.toFixed(2)}</p>
          <p className="text-gray-500 text-xs mt-1">Hook: ${PRICING.hookRate} + Enroute: {form.enroute_miles || 0} mi x ${PRICING.enroutePerMile} + Loaded: {form.loaded_miles || 0} mi x ${PRICING.loadedPerMile}</p>
        </div>
      )}
    </div>
  );
}

function PricingSidebar() {
  return (
    <div className="space-y-6">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="pricing-sidebar">
        <h3 className="text-lg font-bold text-white mb-4">Pricing Information</h3>
        <div className="mb-4 pb-4 border-b border-zinc-800">
          <div className="text-sm text-gray-400 mb-1">Towing Services</div>
          <div className="text-2xl font-bold text-red-600">$150</div>
          <p className="text-gray-500 text-xs">Base Hook Rate</p>
        </div>
        <div className="mb-4 pb-4 border-b border-zinc-800 space-y-1">
          <p className="text-gray-400 text-sm flex justify-between">Enroute <span className="text-white font-semibold">$4/mile</span></p>
          <p className="text-gray-400 text-sm flex justify-between">Loaded <span className="text-white font-semibold">$5/mile</span></p>
        </div>
        <div>
          <div className="text-sm text-gray-400 mb-1">Roadside Services</div>
          <div className="text-2xl font-bold text-red-600">$75</div>
          <p className="text-gray-500 text-xs">Flat rate</p>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
        <h3 className="text-lg font-bold text-white mb-2">Need Immediate Help?</h3>
        <p className="text-red-600 text-sm font-semibold mb-4">Emergency Service Available 24/7</p>
        <a href="tel:+19162343923"><button className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-md font-semibold inline-flex items-center justify-center gap-2"><Phone className="w-4 h-4" /> Call Now: (916) 234-3923</button></a>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="service-areas-sidebar">
        <h3 className="text-sm font-bold text-white mb-3">We Serve</h3>
        <ul className="space-y-1 text-gray-400 text-sm">
          {SERVICE_AREAS.map(a => (
            <li key={a}>- {a}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function QuotePage() {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', service_type: '', urgent: false, address: '', city: '', zip_code: '', vehicle_year: '', vehicle_make: '', vehicle_model: '', vehicle_color: '', notes: '', enroute_miles: '', loaded_miles: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }, []); // setForm is stable (React guarantee), destructured vars are from event param

  const estimate = useMemo(
    () => calcEstimate(form.service_type, form.enroute_miles, form.loaded_miles),
    [form.service_type, form.enroute_miles, form.loaded_miles] // calcEstimate is a module-level pure function
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      setSubmitted(true);
    } catch (err) {
      // Error handled silently - user sees form state
    }
    setLoading(false);
  };

  const showMileage = form.service_type && form.service_type !== 'roadside';
  const formWithHandler = { ...form, onChange: handleChange };

  return (
    <div className="min-h-screen bg-black pt-16" data-testid="quote-page">
      <SEOHead
        title="Request a Quote - Free Towing & Roadside Assistance Estimate"
        description="Free towing quote in Orangevale, CA. Towing from $150, Roadside $75. Request online or call (916) 234-3923 for instant service."
        canonical="/quote"
        schema={getBreadcrumbSchema([{ name: "Home", path: "/" }, { name: "Request Quote", path: "/quote" }])}
      />

      <section className="pt-16 pb-12 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="inline-block mb-4 px-4 py-2 bg-red-600/10 border border-red-600/30 rounded-full">
            <span className="text-red-600 text-sm font-semibold">Request Service</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="quote-heading">Free Towing Quote - Orangevale & Sacramento County</h1>
          <p className="text-gray-400 text-lg">Fill out the form below and we'll get back to you with a quote. For immediate assistance, call us at <a href="tel:+19162343923" className="text-red-600 font-semibold">(916) 234-3923</a>.</p>
        </div>
      </section>

      <section className="py-12 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              {submitted ? (
                <QuoteSuccess />
              ) : (
                <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 space-y-6" data-testid="quote-form">
                  <h2 className="text-xl font-bold text-white">Service Request Form</h2>

                  <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="text-gray-400 text-sm mb-1 block">First Name *</label><input name="first_name" value={form.first_name} onChange={handleChange} required className={INPUT_CLASS} data-testid="input-first-name" /></div>
                    <div><label className="text-gray-400 text-sm mb-1 block">Last Name *</label><input name="last_name" value={form.last_name} onChange={handleChange} required className={INPUT_CLASS} data-testid="input-last-name" /></div>
                    <div><label className="text-gray-400 text-sm mb-1 block">Email *</label><input name="email" type="email" value={form.email} onChange={handleChange} required className={INPUT_CLASS} data-testid="input-email" /></div>
                    <div><label className="text-gray-400 text-sm mb-1 block">Phone *</label><input name="phone" value={form.phone} onChange={handleChange} required className={INPUT_CLASS} data-testid="input-phone" /></div>
                  </div>

                  <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider pt-2">Service Information</h3>
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">Service Type *</label>
                    <select name="service_type" value={form.service_type} onChange={handleChange} required className={INPUT_CLASS} data-testid="select-service">
                      <option value="">Select service type</option>
                      <option value="towing">Towing Service</option>
                      <option value="roadside">Roadside Assistance</option>
                      <option value="hauling">Hauling Service</option>
                      <option value="load-transfer">Load Transfer</option>
                    </select>
                  </div>

                  {showMileage && <MileageEstimate form={formWithHandler} estimate={estimate} />}

                  {form.service_type === 'roadside' && (
                    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 text-center" data-testid="roadside-price">
                      <p className="text-gray-400 text-sm mb-1">Roadside Assistance</p>
                      <p className="text-3xl font-bold text-red-600">${PRICING.roadsideFlat}.00</p>
                      <p className="text-gray-500 text-xs mt-1">Flat rate for tire changes, jump starts, fuel delivery, lockout</p>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <input type="checkbox" name="urgent" checked={form.urgent} onChange={handleChange} className="w-4 h-4" data-testid="checkbox-urgent" />
                    <label className="text-red-600 font-semibold text-sm">URGENT! Assistance Needed ASAP</label>
                  </div>

                  <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider pt-2">Current Location</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><label className="text-gray-400 text-sm mb-1 block">Address *</label><input name="address" value={form.address} onChange={handleChange} required className={INPUT_CLASS} data-testid="input-address" /></div>
                    <div><label className="text-gray-400 text-sm mb-1 block">City *</label><input name="city" value={form.city} onChange={handleChange} required className={INPUT_CLASS} data-testid="input-city" /></div>
                    <div><label className="text-gray-400 text-sm mb-1 block">Zip Code *</label><input name="zip_code" value={form.zip_code} onChange={handleChange} required className={INPUT_CLASS} data-testid="input-zip" /></div>
                  </div>

                  <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider pt-2">Vehicle Information</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div><label className="text-gray-400 text-sm mb-1 block">Year</label><input name="vehicle_year" value={form.vehicle_year} onChange={handleChange} className={INPUT_CLASS} /></div>
                    <div><label className="text-gray-400 text-sm mb-1 block">Make</label><input name="vehicle_make" value={form.vehicle_make} onChange={handleChange} className={INPUT_CLASS} /></div>
                    <div><label className="text-gray-400 text-sm mb-1 block">Model</label><input name="vehicle_model" value={form.vehicle_model} onChange={handleChange} className={INPUT_CLASS} /></div>
                    <div><label className="text-gray-400 text-sm mb-1 block">Color</label><input name="vehicle_color" value={form.vehicle_color} onChange={handleChange} className={INPUT_CLASS} /></div>
                  </div>

                  <div><label className="text-gray-400 text-sm mb-1 block">Additional Notes</label><textarea name="notes" value={form.notes} onChange={handleChange} rows={3} className={`${INPUT_CLASS} resize-none`} data-testid="input-notes" /></div>

                  <button type="submit" disabled={loading} className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-md font-semibold text-lg transition-all disabled:opacity-50" data-testid="submit-quote-btn">
                    {loading ? 'Submitting...' : 'Request Service'}
                  </button>
                </form>
              )}
            </div>

            <PricingSidebar />
          </div>
        </div>
      </section>
    </div>
  );
}

export default QuotePage;
